/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { jsx, cssMap } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { useSmartLinkContext } from '@atlaskit/link-provider/context';
import type { BatchResponse, SuccessResponse } from '@atlaskit/link-provider/responses';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card } from '@atlaskit/smart-card/card/lazy';
import { FooterBlock } from '@atlaskit/smart-card/flexible/footer-block';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import { token } from '@atlaskit/tokens';

/** ARI prefix for Loom sources, which are turned into share URLs directly. */
const LOOM_ARI_PREFIX = 'ari:cloud:loom';

/**
 * Loom ARIs are currently not supported by the resolver,
 * this is a workaround until support is added.
 *
 * Note that this won't work for staging aris.
 */
function loomAriToUrl(ari: string): string {
	const id = ari.split('/').pop() ?? '';
	return `https://loom.com/share/${id}`;
}

// Hide the smart card's own chrome so it sits cleanly inside our own container.
const cardUiOptions = {
	hidePadding: true,
	hideElevation: true,
	hideBackground: true,
	clickableContainer: true,
};

const styles = cssMap({
	sourcesContainer: {
		overflow: 'hidden',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	sourceCard: {
		position: 'relative',
		boxSizing: 'border-box',
		overflow: 'hidden',
		width: '100%',
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		minHeight: '58px', // reserve space (single-line title + footer) to avoid layout shift while resolving
	},
	// Interactive affordance, only applied once there's a resolvable URL — not while
	// the skeleton is showing (where the box isn't clickable yet).
	sourceCardInteractive: {
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	// Keep the card mounted (so its lazy IntersectionObserver still fires and it
	// resolves) but hidden, so the raw URL doesn't flash before it resolves.
	cardLoading: {
		visibility: 'hidden',
	},
	// Skeleton overlay shown while the card resolves. Absolutely positioned so it
	// sits on top of the (hidden) card without adding to the reserved height.
	skeletonOverlay: {
		position: 'absolute',
		inset: token('space.0'),
		boxSizing: 'border-box',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		gap: token('space.075'),
		pointerEvents: 'none',
	},
});

/** Type guard for a successful ARI batch response entry. */
function isSuccessResponse(r: BatchResponse[number]): r is SuccessResponse {
	return r.status === 200 && 'body' in r;
}

/** Shape of the parts of an ARI batch response body we read a URL from. */
type ResolvedSourceBody = { data?: { url?: string }; url?: string };

/** Extracts the resolved URL from a successful ARI batch response. */
function extractUrl(r: SuccessResponse): string | undefined {
	const body = r.body as ResolvedSourceBody | undefined;
	return body?.data?.url ?? body?.url;
}

export type SuggestionCardSourcesProps = {
	sourceAris: string[];
};

// Render nothing if resolving sources throws — most notably when this is mounted
// without an ancestor <SmartCardProvider> (e.g. outside the editor), where
// useSmartLinkContext throws. Hoisted so the reference is stable across renders.
const renderNoSources = (): null => null;

/** Skeleton stand-in for the card's title and footer while the URL resolves. */
function SourceSkeleton(): React.JSX.Element {
	return (
		<div css={styles.skeletonOverlay} aria-hidden={true}>
			<Skeleton width="65%" height={12} borderRadius={token('radius.small')} isShimmering />
			<Skeleton width="40%" height={10} borderRadius={token('radius.small')} isShimmering />
		</div>
	);
}

function SourceCard({ url }: { url: string }): React.JSX.Element | null {
	const [status, setStatus] = React.useState<'loading' | 'resolved' | 'errored'>('loading');
	const handleResolve = React.useCallback(() => setStatus('resolved'), []);
	const handleError = React.useCallback(() => setStatus('errored'), []);

	if (status === 'errored') {
		return null;
	}

	return (
		<>
			<div css={[status === 'loading' && styles.cardLoading]}>
				<Card
					appearance="block"
					ui={cardUiOptions}
					url={url}
					onResolve={handleResolve}
					onError={handleError}
				>
					<TitleBlock maxLines={1} hideIcon={true} anchorTarget="_blank" />
					<FooterBlock />
				</Card>
			</div>
			{status === 'loading' && <SourceSkeleton />}
		</>
	);
}

function SuggestionCardSourcesInner({
	sourceAris,
}: SuggestionCardSourcesProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();
	const { connections } = useSmartLinkContext();
	const { client } = connections;

	// We only show the first source for now (multiple sources aren't supported yet).
	// Loom ARIs become a share URL directly; everything else is resolved through the
	// smart-link context.
	const firstAri = sourceAris[0];
	const isLoom = firstAri?.startsWith(LOOM_ARI_PREFIX) ?? false;
	const needsResolving = Boolean(firstAri) && !isLoom;

	const [resolvedUrl, setResolvedUrl] = React.useState<string | undefined>(undefined);
	// Initialise from props so the skeleton reserves space on the first paint for a
	// non-Loom ARI (avoids a null → skeleton flash and the SuggestionCard expanding).
	const [isResolving, setIsResolving] = React.useState(needsResolving);

	React.useEffect(() => {
		// Loom (or missing) ARIs don't need resolving.
		if (!needsResolving) {
			setResolvedUrl(undefined);
			setIsResolving(false);
			return;
		}

		let cancelled = false;
		// Clear any URL resolved for a previous ARI and show the skeleton again.
		setResolvedUrl(undefined);
		setIsResolving(true);
		client
			.fetchDataAris([firstAri])
			.then((responses) => {
				if (cancelled) {
					return;
				}
				const url = responses.filter(isSuccessResponse).map(extractUrl).find(Boolean);
				setResolvedUrl(url);
			})
			.catch(() => {
				// Network or resolution errors fall through to the "no URL" case below,
				// which hides the section rather than crashing.
			})
			.finally(() => {
				if (!cancelled) {
					setIsResolving(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [firstAri, needsResolving, client]);

	const url = firstAri && isLoom ? loomAriToUrl(firstAri) : resolvedUrl;

	// Nothing to show: no source ARI, or a non-Loom ARI that finished resolving
	// without a usable URL (failed or empty).
	if (!url && !isResolving) {
		return null;
	}

	return (
		<div css={styles.sourcesContainer}>
			<Inline space="space.050" alignBlock="center">
				<TeamworkGraphIcon label="" size="small" color={token('color.icon.subtle')} />
				<Text as="span" color="color.text.subtle" size="small">
					{formatMessage(aiSuggestionsMessages.sourceLabel)}
				</Text>
			</Inline>
			<div css={[styles.sourceCard, url && styles.sourceCardInteractive]}>
				{url ? <SourceCard key={url} url={url} /> : <SourceSkeleton />}
			</div>
		</div>
	);
}

export function SuggestionCardSources({
	sourceAris,
}: SuggestionCardSourcesProps): React.JSX.Element {
	return (
		<ErrorBoundary fallbackRender={renderNoSources}>
			<SuggestionCardSourcesInner sourceAris={sourceAris} />
		</ErrorBoundary>
	);
}
